import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, Bell, Smile, Brain, Dumbbell, Sparkles, Globe, Heart, Users, Palette, Wifi, WifiOff, Mic, CheckCircle } from 'lucide-react';
import { BigButton } from '../components/ui/BigButton';
import { WeatherWidget } from '../components/ui/WeatherWidget';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { StaggerContainer, StaggerItem, staggerItemVariants } from '../components/ui/PageTransition';
import { supabase } from '../lib/supabase';

// Animation variants
const containerVariants = {
  initial: {},
  enter: {
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  initial: { opacity: 0, y: 20 },
  enter: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

export const Home = () => {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [okayLogged, setOkayLogged] = useState(false);
  const [checking, setChecking] = useState(true);

  // Online/offline detection
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check if user already tapped "I'm Okay" today
  useEffect(() => {
    const checkToday = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setChecking(false); return; }
      const today = new Date().toISOString().split('T')[0];
      const { data } = await supabase
        .from('activity_log')
        .select('id')
        .eq('user_id', user.id)
        .eq('action', 'okay_today')
        .gte('created_at', today)
        .limit(1);
      if (data && data.length > 0) setOkayLogged(true);
      setChecking(false);
    };
    checkToday();
  }, []);
  const handleOkayToday = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    await supabase.from('activity_log').insert({
      user_id: user.id,
      action: 'okay_today',
      metadata: { timestamp: new Date().toISOString() }
    });
    setOkayLogged(true);
  };

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('goodMorning');
    if (hour < 17) return t('goodAfternoon');
    return t('goodEvening');
  };

  return (
    <motion.div 
      className="flex-grow space-y-6 pb-6"
      style={{ contain: 'layout' }}
      variants={containerVariants}
      initial="initial"
      animate="enter"
    >
      {/* Offline Indicator */}
      {!isOnline && (
        <motion.div 
          variants={itemVariants}
          className="bg-accent/20 border-2 border-accent/40 rounded-2xl p-3 flex items-center justify-center gap-2"
        >
          <WifiOff size={20} className="text-accent" />
          <span className="text-sm font-medium text-primary">{t('offlineMode')}</span>
        </motion.div>
      )}

      {/* Weather Widget */}
      <motion.div variants={itemVariants}>
        <WeatherWidget />
      </motion.div>

      {/* Welcome Card */}
      <motion.div 
        variants={itemVariants}
        className="card-warm card-stable text-center"
      >
        <p className="text-accent text-sm mb-1">{t('welcome')}</p>
        <h2 className="text-senior-xl font-bold text-primary font-serif">
          {greeting()}, {t('friend')}
        </h2>
        <p className="text-sm text-text-secondary mt-2">
          {t('youreDoingWonderfully')}
        </p>
      </motion.div>
      {/* I'm Okay Today Button */}
      {!checking && (
        <motion.div variants={itemVariants}>
          <motion.button
            onClick={handleOkayToday}
            disabled={okayLogged}
            whileHover={okayLogged ? {} : { scale: 1.02 }}
            whileTap={okayLogged ? {} : { scale: 0.98 }}
            className={`w-full p-5 rounded-3xl flex items-center justify-center gap-3 shadow-warm text-white text-xl font-bold transition-all ${
              okayLogged
                ? 'bg-green-500 cursor-default'
                : 'bg-gradient-to-r from-green-500 to-emerald-600'
            }`}
          >
            <CheckCircle size={32} />
            {okayLogged ? "You're all good today!" : "I'm Okay Today"}
          </motion.button>
        </motion.div>
      )}
      {/* Main Actions */}
      <div className="space-y-4">
        {/* Chat - Full Width */}
        <BigButton
          label={t('talkToMe')}
          sublabel={t('imHereToListen')}
          icon={<MessageCircle />}
          onClick={() => navigate('/senior/chat')}
          fullWidth
        />

        {/* 2x2 Grid */}
        <div className="grid grid-cols-2 gap-4">
          <BigButton
            label={t('reminders')}
            variant="water"
            icon={<Bell />}
            onClick={() => navigate('/senior/reminders')}
            compact
          />
          <BigButton
            label={t('myMood')}
            variant="outline"
            icon={<Smile />}
            onClick={() => navigate('/senior/mood')}
            compact
          />
          <BigButton
            label={t('exercise')}
            variant="exercise"
            icon={<Dumbbell />}
            onClick={() => navigate('/senior/exercises')}
            compact
          />
          <BigButton
            label={t('brainGames')}
            variant="secondary"
            icon={<Brain />}
            onClick={() => navigate('/senior/games')}
            compact
          />
        </div>

        {/* New Features Row */}
        <div className="grid grid-cols-2 gap-4">
          <motion.button
            onClick={() => navigate('/senior/wisdom')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="p-4 bg-gradient-to-br from-violet-500 to-purple-600 text-white rounded-3xl flex flex-col items-center gap-2 shadow-warm"
          >
            <Sparkles size={32} />
            <span className="text-lg font-bold">{t('dailyWisdom')}</span>
            <span className="text-xs opacity-80">{t('inspiringQuotes') || 'Inspiring quotes'}</span>
          </motion.button>
          <motion.button
            onClick={() => navigate('/senior/health-resources')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="p-4 bg-gradient-to-br from-red-500 to-rose-600 text-white rounded-3xl flex flex-col items-center gap-2 shadow-warm"
          >
            <Heart size={32} />
            <span className="text-lg font-bold">{t('healthResources')}</span>
            <span className="text-xs opacity-80">{t('emergencyContacts') || 'Emergency contacts'}</span>
          </motion.button>
        </div>
        {/* Family Sharing Button */}
        <motion.button
          onClick={() => navigate('/senior/family')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full p-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-3xl flex items-center justify-center gap-3 shadow-warm"
        >
          <Users size={28} />
          <div className="text-left">
            <p className="text-lg font-bold">{t('familySharing') || 'Family Sharing'}</p>
            <p className="text-xs opacity-80">{t('connectWithLovedOnes') || 'Connect with loved ones'}</p>
          </div>
        </motion.button>

        {/* Hobbies Button */}
        <motion.button
          onClick={() => navigate('/senior/hobbies')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full p-4 bg-gradient-to-r from-pink-500 to-orange-500 text-white rounded-3xl flex items-center justify-center gap-3 shadow-warm"
        >
          <Palette size={28} />
          <div className="text-left">
            <p className="text-lg font-bold">{t('hobbies') || 'My Hobbies'}</p>
            <p className="text-xs opacity-80">{t('activitiesThatBringJoy') || 'Activities that bring joy'}</p>
          </div>
        </motion.button>

        {/* {t('voiceCommands')} Tutorial Button */}
        <motion.button
          onClick={() => navigate('/senior/voice-tutorial')}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          className="w-full p-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-3xl flex items-center justify-center gap-3 shadow-warm"
        >
          <Mic size={28} />
          <div className="text-left">
            <p className="text-lg font-bold">{t('voiceCommands')}</p>
            <p className="text-xs opacity-80">{t('learnHandsfreeControls')}</p>
          </div>
        </motion.button>
      </div>
    </motion.div>
  );
};
